"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useCallback, useEffect, useState } from "react";
import { cn } from "utils";
import log from "logbeacon";
import { EdgenModalCreateAble } from "../../EdgenModal";
import EdgenDrawerCallable from "../../EdgenDrawerCallable";
import {
  UpgradeReminderModalCreateAbleProps,
  UpgradeReminderModalResult,
  UpgradeReminderProps,
  UpgradeScenarioConfig,
} from "./types";

import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/modal/UpgradeReminder/UpgradeReminderModal";
import { openEmailClient } from "@/src/utils";
import { useMemberFeature, useMemberUser, useShellRouter } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);
/**
 * Get configuration based on scenario and membership info
 */

const getScenarioConfig = (
  scenario: UpgradeReminderProps["scenario"],
  memberInfo: any,
  isTrial: boolean,
): UpgradeScenarioConfig => {
  const { isFree, isPro, isExpires } = memberInfo || {};
  const contentMap = {
    title: I18n.t("Limits Are Boring. Go Expert."),
    content: [
      I18n.t("Increased Search"),
      I18n.t("Powerful Trading Tools"),
      I18n.t("Access to Edgen Report"),
      I18n.t("Earn More Aura Rewards"),
    ],
  };
  const switchContentMap = {
    title: I18n.t("Contact us to upgrade your plan"),
    content: [
      I18n.t("More Searches"),
      I18n.t("Extra Aura"),
      I18n.t("Powerful Trading Tools"),
    ],
  };

  // 360 report 权益用完
  const reportContentMap = {
    title: I18n.t("Monthly Edgen Report limit reached"),
    content: I18n.t(
      "You can still dive deeper into all the details about tokens and stocks!",
    ),
  };
  const scenarioConfigMap = {
    expertRealTimeReports: {
      title: I18n.t("Upgrade to Get Real-Time Results."),
      content: I18n.t("Contact us for an Expert plan."),
      buttonText: I18n.t("Contact Us"),
      buttonAction: "contactSales" as const,
    },

    noSearchCount: {
      free: {
        expired: {
          ...contentMap,
          buttonText: I18n.t("Upgrade"),
          buttonAction: "upgrade" as const,
        },
        active: {
          ...contentMap,
          buttonText: isTrial ? I18n.t("Start $1 Trial") : I18n.t("Upgrade"),
          buttonAction: "startTrial" as const,
        },
      },
      pro: {
        ...contentMap,
        buttonText: I18n.t("Contact Us"),
        buttonAction: "contactSales" as const,
      },
    },

    premiumTradingSignals: {
      title: I18n.t(
        "Become an Expert member to unlock premium trading signals.",
      ),
      content: I18n.t("Contact us for Upgrade"),
      buttonText: I18n.t("Contact Us"),
      buttonAction: "contactSales" as const,
    },
    enterpriseRealTimeReports: {
      ...switchContentMap,
      buttonText: I18n.t("Contact Us"),
      buttonAction: "contactSales" as const,
    },
    noReportCount: {
      ...reportContentMap,
      buttonText: I18n.t("Check Details"),
      buttonAction: "checkDetails" as const,
    },
  };
  switch (scenario) {
    case "expert-realTimeReports":
      return scenarioConfigMap.expertRealTimeReports;

    case "noSearchCount":
      if (isFree) {
        return isExpires
          ? scenarioConfigMap.noSearchCount.free.expired
          : scenarioConfigMap.noSearchCount.free.active;
      } else if (isPro) {
        return scenarioConfigMap.noSearchCount.pro;
      }
      break;

    case "premiumTradingSignals":
      return scenarioConfigMap.premiumTradingSignals;

    case "enterprise-realTimeReports":
      return scenarioConfigMap.enterpriseRealTimeReports;

    case "noReportCount":
      return scenarioConfigMap.noReportCount;

    default:
      return scenarioConfigMap.noSearchCount.free.active;
  }

  // Fallback configuration
  return scenarioConfigMap.noSearchCount.free.active;
};

/**
 * Upgrade Reminder Modal Component
 * Uses OspModal for consistent modal experience across the app
 */
const UpgradeReminderModal: React.FC<UpgradeReminderProps> = ({
  onClose,
  scenario,
  customConfig,
  onButtonClick,
}) => {
  // Use hook to get member information instead of props
  const { data: memberInfo } = useMemberUser();
  const { isTrial } = useMemberFeature();
  const router = useShellRouter();
  const [isLoading, setIsLoading] = useState(false);
  // Get default configuration based on scenario and membership info
  const defaultConfig = getScenarioConfig(scenario, memberInfo, !!isTrial);

  // Merge with custom configuration if provided
  const config = { ...defaultConfig, ...customConfig };

  const isContentArray = Array.isArray(config.content);
  const features = isContentArray ? (config.content as string[]) : [];
  const description = config.desc ? (config.desc as string) : null;
  const highlightFirstItem = config.highlightFirstItem ?? false;

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      return () => {
        setIsLoading(false);
        clearTimeout(timer);
      };
    }
  }, [isLoading]);

  const onClick = useCallback(() => {
    let delayClose = false;
    if (config.buttonAction === "explore") {
      onClose?.();
      return;
    }
    if (
      config.buttonAction === "contactSales" ||
      config.buttonAction === "contactOurTeam"
    ) {
      // 使用discord链接打开方案
      // if (memberInfo?.isExpert) {
      //   openDiscordLink();
      //   return;
      // } else {
      openEmailClient();
      // }
    } else if (
      config.buttonAction === "startTrial" ||
      config.buttonAction === "upgrade"
    ) {
      log.info(
        "[member][entry]: upgrade reminder modal button click to goto buy member",
      );
      router.push("/member");
    } else if (config.buttonAction === "checkDetails") {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      delayClose = true;
      log.info("[UpgradeReminderModal] checkDetails button click");
      // 当按钮行为为 "checkDetails" 时，回调给外部处理
      onButtonClick?.();
      setTimeout(() => {
        delayClose = false;
        onClose?.();
      }, 3000);
    }
    !delayClose && onClose?.();
  }, [config.buttonAction, onClose, router, onButtonClick, isLoading]);

  return (
    <div className="relative flex w-full flex-col items-start gap-6 overflow-hidden p-6">
      {/* Background Images */}
      <Icon
        data-openreplay-label="close"
        name="close"
        size={20}
        onClick={onClose}
        className="text-content_tertiary md:hover:text-content_secondary absolute right-3 top-3 cursor-pointer"
      />
      {/* <NextImage data-openreplay-label="UpgradeReminder · UpgradeReminderModal · NextImage"
        src="/assets/images/close-back-cover.png"
        className="absolute right-3 top-3 cursor-pointer w-5 h-5"
        onClick={onClose}
      /> */}
      {/* Title */}
      <div className="relative whitespace-pre-line  gap-1">
        <p className="headline_regular_s text-content_primary">
          {config.title}
        </p>
        {/* Description Text */}
        {description && (
          <div className="body_l text-content_secondary">{description}</div>
        )}
      </div>

      {/* Content Section */}
      <div className="relative flex w-full flex-col gap-6">
        {/* Features List */}
        {isContentArray && features.length > 0 && (
          <div className="flex w-full flex-col gap-1">
            {features.map((feature, index) => (
              <div key={index} className="flex w-full items-center gap-1">
                {features.length > 1 && (
                  <Icon
                    name="check"
                    size={16}
                    className={cn("text-content_primary", {
                      "text-content_brand_primary":
                        highlightFirstItem && index === 0,
                    })}
                  />
                )}
                <span
                  className={cn({
                    "body_m text-content_tertiary": !(
                      highlightFirstItem && index === 0
                    ),
                    "text-gradient title_m": highlightFirstItem && index === 0,
                  })}
                >
                  {feature}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Button */}
      <button
        data-openreplay-label={`UpgradeReminderModal · Button | ${config.buttonText}`}
        onClick={onClick}
        className="w-full button_m md_button emphasis_button button z-100 group"
        disabled={isLoading}
        data-loading={isLoading}
      >
        <span className="group-loading:hidden">{config.buttonText}</span>
        <Icon
          name="ic_loading2"
          size={20}
          color={ospThemes.colors.content_inverse_primary}
          className={cn("hidden animate-spin group-loading:block")}
        />
      </button>
    </div>
  );
};

/**
 * 可创建的 UpgradeReminderModal
 * 支持外部随用随调，无需绑定在组件上
 */
export const UpgradeReminderModalCreateAble = async (
  props: UpgradeReminderModalCreateAbleProps,
): Promise<UpgradeReminderModalResult> => {
  const { modalProps, ...otherProps } = props;

  return new Promise((resolve) => {
    EdgenModalCreateAble.call({
      children: (
        <UpgradeReminderModal
          data-openreplay-label="UpgradeReminder · UpgradeReminderModal · UpgradeReminderModal"
          {...otherProps}
          onClose={() => {
            EdgenModalCreateAble.end(false);
            EdgenDrawerCallable.end(false);
            resolve({
              action: "close",
              scenario:
                otherProps.scenario || otherProps?.customConfig?.title || "",
            });
          }}
          onButtonClick={async () => {
            // 先执行外部的 onButtonClick
            if (otherProps.onButtonClick) {
              try {
                await otherProps.onButtonClick();
              } catch (error) {
                log.error("[文案]onButtonClick error:", error);
              }
            }
            // 然后结束弹窗
            EdgenModalCreateAble.end(false);
            EdgenDrawerCallable.end(false);
            resolve({
              action: "buttonClick",
              scenario:
                otherProps.scenario || otherProps?.customConfig?.title || "",
            });
          }}
        />
      ),
      containerClassName: "!md:w-[400px] rounded-2xl",
      contentStyle: {
        borderRadius: 16,
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        padding: 0,
      },
      titleStyle: {
        minHeight: 0,
        backgroundColor: "#1A1A1A",
      },
      centered: true,
      showClose: false,
      destroyOnClose: true,
      ...modalProps,
    });
  });
};
